import React, {useRef, useEffect, useState, useContext, useMemo} from 'react';
import {Sidebar} from "./Sidebar";
import {Section} from "./Section";
import CloseIcon from '@mui/icons-material/Close';
import styles from "../../../styles/components/Modal/Filter/ModalFilter.module.css";
import { Notification } from '../../UI/Notifications';
import { LangContext } from '../../../context/LangContext';
import { ThemeContext } from '../../../context/ThemeContext';
import { saveWithTTL } from '../../../utils/custom/localStorageTTL';

const BASE_LEAD_STORAGE_KEY = 'leadFiltersState';
const SAVE_FILTERS_SETTING_KEY = 'leadSaveFiltersSetting';

const translations = {
    ru: {
        title: "Фильтры",
        reset: "Сбросить",
        confirm: "Подтвердить",
        saveSetting: "Сохранять фильтры между сессиями",
    },
    en: {
        title: "Filters",
        reset: "Reset",
        confirm: "Confirm",
        saveSetting: "Save filters between sessions",
    },
};

const getDefaultFilters = () => ({
    manager_ids: [],
    desk_ids: [],
    affiliator_ids: [],
    campaign_ids: [],
    reten_statuses: [],
    sale_statuses: [],
    geos: [],
    search: '',
    from_date: '',
    to_date: '',
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    order_by: '',
});


const cloneFilters = (f) => ({
    ...getDefaultFilters(),
    ...f,
    manager_ids: Array.isArray(f?.manager_ids) ? [...f.manager_ids] : [],
    desk_ids: Array.isArray(f?.desk_ids) ? [...f.desk_ids] : [],
    affiliator_ids: Array.isArray(f?.affiliator_ids) ? [...f.affiliator_ids] : [],
    campaign_ids: Array.isArray(f?.campaign_ids) ? [...f.campaign_ids] : [],
    reten_statuses: Array.isArray(f?.reten_statuses) ? [...f.reten_statuses] : [],
    sale_statuses: Array.isArray(f?.sale_statuses) ? [...f.sale_statuses] : [],
    geos: Array.isArray(f?.geos) ? [...f.geos] : [],
});

const getPersistedLeadFilters = (filters) => {
    if (!filters || typeof filters !== 'object') {
        return filters;
    }

    const { search, ...persistedFilters } = filters;
    void search;

    return persistedFilters;
};

export const ModalFilter = ({
                                isActiveFilter,
                                setIsActiveFilter,
                                filters,
                                onApplyFilters,
                                role,
                                canListAffiliatorAccounts
                            }) => {
    const {langSelected} = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const t = translations[langSelected] || translations.ru;

    const modalRef = useRef(null);
    const LEAD_STORAGE_KEY_WITH_ROLE = useMemo(() => `${BASE_LEAD_STORAGE_KEY}_${role}`, [role]);

    const [shouldSaveFilters, setShouldSaveFilters] = useState(() => {
        const storedSetting = localStorage.getItem(SAVE_FILTERS_SETTING_KEY);
        return storedSetting === null ? true : storedSetting === 'true';
    });

    const [draftFilters, setDraftFilters] = useState(() => cloneFilters(filters));


    useEffect(() => {
        if (!isActiveFilter) return;
        setDraftFilters(cloneFilters(filters));
    }, [isActiveFilter, filters]);

    const handleToggleSaveSetting = () => {
        const newState = !shouldSaveFilters;
        setShouldSaveFilters(newState);
        localStorage.setItem(SAVE_FILTERS_SETTING_KEY, newState ? 'true' : 'false');
    };

    const handleReset = () => {
        setDraftFilters(getDefaultFilters());
    };

    const handleConfirm = () => {
        const toApply = cloneFilters(draftFilters);

        if (shouldSaveFilters) {
            saveWithTTL(LEAD_STORAGE_KEY_WITH_ROLE, getPersistedLeadFilters(toApply));
        } else {
            localStorage.removeItem(LEAD_STORAGE_KEY_WITH_ROLE);
        }

        onApplyFilters(toApply);
    };

    const handleClose = () => {
        setIsActiveFilter(false);
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            handleClose();
        }
    };


    const modalClass = `${styles.modal} ${theme === 'dark' ? styles.darkModal : ''}`;
    const containerClass = `${styles.filtersContainer} ${theme === 'dark' ? styles.darkFiltersContainer : ''}`;
    const labelClass = `${styles.modalLabel} ${theme === 'dark' ? styles.darkModalLabel : ''}`;
    const footerClass = `${styles.modalFooter} ${theme === 'dark' ? styles.darkModalFooter : ''}`;
    const saveLabelClass = `${styles.saveSettingLabel} ${theme === 'dark' ? styles.darkSaveSettingLabel : ''}`;

    return (
        <div className={modalClass} onMouseDown={handleOverlayClick}>
            <div className={containerClass} ref={modalRef}>
                <div className={labelClass}>
                    <div>
                        <p>{t.title}</p>
                    </div>
                    <div onClick={handleClose} style={{cursor: 'pointer'}}>
                        <CloseIcon className={theme === 'dark' ? styles.darkCloseIcon : ''} />
                    </div>
                </div>

                <div className={styles.modalContent}>
                    <Sidebar/>
                    <div className={styles.section}>
                        <Section
                            filters={draftFilters}
                            setFilters={setDraftFilters}
                            role={role}
                            canListAffiliatorAccounts={canListAffiliatorAccounts}
                        />
                    </div>
                </div>
                
                <div className={footerClass}>
                    <div className={styles.saveSettingContainer}>
                        <input
                            type="checkbox"
                            id="saveFiltersCheckbox"
                            checked={shouldSaveFilters}
                            onChange={handleToggleSaveSetting}
                            className={styles.saveSettingCheckbox}
                        />
                        <label htmlFor="saveFiltersCheckbox" className={saveLabelClass}>
                            {t.saveSetting}
                        </label>
                    </div>

                    <div className={`${styles.resetWrapper} ${theme === 'dark' ? styles.darkResetWrapper : ''}`}>
                        <button className={styles.clearButton} onClick={handleReset}>{t.reset}</button>
                    </div>
                    <div className={`${styles.confirmationWrapper} ${theme === 'dark' ? styles.darkConfirmationWrapper : ''}`}>
                        <button className={styles.submitButton} onClick={handleConfirm}>{t.confirm}</button>
                    </div>
                </div>
            </div>
        </div>
    );
};
